"use client";

import {
  Button,
  Flex,
  Input,
  Text,
  useMediaQuery,
  HStack,
  Circle,
  Spinner,
} from "@chakra-ui/react";
// 🌟 Importação de useRef
import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useState, useRef } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { PRIMARY_COLOR } from "../../constants/colors";
import api from "../../services/api";
import { toast } from "react-toastify";
import useCart from "../../hooks/useCart";
import { AuthContext } from "../../contexts/AuthContext";
import { isValidCpfCnpj, maskCpfCnpj, onlyDigits } from "../../utils/cpfCnpj";
import { isClientUser } from "../../utils/userRole";
import { buildRegisterPath } from "../../utils/catalogContext";

function maskPhone(value) {
  // Remove tudo que não é número
  value = value.replace(/\D/g, "");

  // Limita a 11 dígitos (padrão celular)
  value = value.slice(0, 11);

  // (XX
  if (value.length <= 2) {
    return `(${value}`;
  }

  // (XX) XXXX
  if (value.length <= 6) {
    return `(${value.slice(0, 2)}) ${value.slice(2)}`;
  }

  // (XX) XXXXX-XXXX
  if (value.length <= 10) {
    return `(${value.slice(0, 2)}) ${value.slice(2, 6)}-${value.slice(6)}`;
  }

  // (XX) XXXXX-XXXX para 11 dígitos
  return `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7)}`;
}

export default function Checkout() {
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const navigate = useNavigate();
  const { clientHash, sellerId } = useParams();
  const { clearCart, cart, totalCartValue } = useCart(clientHash, sellerId);
  const { user, setUser } = useContext(AuthContext);

  const [cpfCnpj, setCpfCnpj] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [orderError, setOrderError] = useState(null);

  // 🌟 Referência para evitar a duplicação do pedido automático
  const hasCreatedOrderRef = useRef(false);

  const [form, setForm] = useState({
    email: "",
    name: "",
    phone: "",
    instagram: "",
    password: "",
  });

  // --- NOVOS STATES APENAS PARA O FLUXO DE LOGIN (cliente existente) ---
  const [passwordLogin, setPasswordLogin] = useState("");
  const [clientFoundData, setClientFoundData] = useState(null);
  // -------------------------------------------------------------------

  const steps = ["Identificação", "Cadastro", "Confirmação"];

  // 🛑 Efeito ajustado para criar o pedido para usuário logado
  const createOrderForLoggedUser = async () => {
    // 🌟 Usa a referência persistente para evitar duplicação
    if (hasCreatedOrderRef.current) return;

    if (isClientUser(user) && user?.info?.email && cart.length > 0) {
      hasCreatedOrderRef.current = true; // Marca como iniciado
      setLoadingOrder(true);
      setOrderError(null);

      try {
        const response = await api.post("/orders", {
          clientId: user.info.id,
          products: cart,
          client_hash: clientHash,
          sellerId: Number(sellerId),
        });

        clearCart();
        setCurrentStep(2);
        setCreatedOrderId(response.data.order.id);
        if (response.data.stockAdjustments?.length > 0) {
          toast.warn(
            "Alguns produtos não puderam ser atendidos integralmente devido à falta de estoque. Consulte o detalhamento completo em Meus Pedidos.",
          );
        }
      } catch (err) {
        // Se falhar, reseta a ref para permitir nova tentativa
        hasCreatedOrderRef.current = false;
        // 🌟 Usuário já está logado: em vez de cair na tela de
        // Identificação (que pede CPF/CNPJ de novo, dando a falsa
        // impressão de que ele "deslogou"), mostramos um erro com opção
        // de tentar novamente.
        setOrderError(
          err.response?.data?.error ??
            "Erro ao criar o pedido. Tente novamente mais tarde.",
        );
        toast.error("Erro ao criar o pedido. Tente novamente mais tarde.");
      } finally {
        setLoadingOrder(false);
      }
    }
  };

  // Admin/master não compra: obriga o cadastro como cliente.
  useEffect(() => {
    if (user && !isClientUser(user)) {
      toast.warning(
        "Você está logado como administrador. Para comprar, faça o cadastro como cliente.",
      );
      navigate(buildRegisterPath(clientHash, sellerId), {
        replace: true,
        state: { from: window.location.pathname },
      });
    }
  }, [user, clientHash, sellerId, navigate]);

  useEffect(() => {
    createOrderForLoggedUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, cart, clientHash, sellerId, clearCart, setCurrentStep]);

  // Funções originais de verificação e cadastro
  const verifyCpfCnpj = async () => {
    if (!isValidCpfCnpj(cpfCnpj)) {
      toast.error("CPF/CNPJ inválido!");
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post("/clients/exist", {
        cpfCnpj: onlyDigits(cpfCnpj),
      });

      if (!data.exist) {
        toast.info("CPF/CNPJ não encontrado, prossiga com o cadastro.");
        setCurrentStep(1);
      } else {
        // --- AJUSTE: SE EXISTIR, PEDIR SENHA ANTES DE CRIAR O PEDIDO ---
        // guardamos os dados retornados do client para usar o email no login
        setClientFoundData(data.client);
        // usamos um step intermediário "login" para pedir a senha
        setCurrentStep("login");
        // não criar pedido aqui
      }
    } catch {
      toast.error("Erro ao verificar CPF/CNPJ!");
    } finally {
      setLoading(false);
    }
  };

  // Função que tenta logar o cliente existente e criar o pedido
  const loginAndCreateOrder = async () => {
    if (!clientFoundData) return;
    setLoading(true);
    try {
      // tenta login com o email do cliente encontrado
      const loginRes = await api.post("/login/clients", {
        email: clientFoundData.email,
        password: passwordLogin,
      });

      hasCreatedOrderRef.current = true;

      // salva usuário - igual ao fluxo de registro
      setUser({
        token: loginRes.data.token,
        info: loginRes.data.client,
      });
      localStorage.setItem("@aqvex/user", JSON.stringify(loginRes.data.client));
      localStorage.setItem("@aqvex/token", JSON.stringify(loginRes.data.token));

      // cria pedido
      const orderRes = await api.post("/orders", {
        clientId: loginRes.data.client.id,
        products: cart,
        client_hash: clientHash,
        sellerId: Number(sellerId),
      });

      // limpeza e navegação igual ao fluxo original
      localStorage.removeItem(`@aqvex/products_${clientHash}`);
      localStorage.removeItem(`@aqvex/products_last_update_${clientHash}`);
      setCreatedOrderId(orderRes.data.order.id);
      clearCart();
      setCurrentStep(2);
      toast.success("Pedido concluído com sucesso!");
      if (orderRes.data.stockAdjustments?.length > 0) {
        toast.warn(
          "Alguns produtos não puderam ser atendidos integralmente devido à falta de estoque. Consulte o detalhamento completo em Meus Pedidos.",
        );
      }
    } catch (error) {
      // se login falhar, mostramos mensagem e permanecemos no step de login
      toast.error(
        error.response?.data?.error ?? "Erro no login. Verifique sua senha.",
      );
    } finally {
      setLoading(false);
    }
  };

  const registerClient = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/clients", {
        name: form.name,
        email: form.email,
        cpf_cnpj: onlyDigits(cpfCnpj),
        instagram: form.instagram,
        password: form.password,
        phone: form.phone,
        client_hash: clientHash,
      });
      const userResponse = await api.post("/login/clients", {
        email: form.email,
        password: form.password,
      });
      hasCreatedOrderRef.current = true;
      setUser({
        token: userResponse.data.token,
        info: userResponse.data.client,
      });
      localStorage.setItem(
        "@aqvex/user",
        JSON.stringify(userResponse.data.client),
      );
      localStorage.setItem(
        "@aqvex/token",
        JSON.stringify(userResponse.data.token),
      );
      const response = await api.post("/orders", {
        clientId: data.id,
        products: cart,
        client_hash: clientHash,
        sellerId: Number(sellerId),
      });
      toast.success("Pedido concluído com sucesso!");
      if (response.data.stockAdjustments?.length > 0) {
        toast.warn(
          "Alguns produtos não puderam ser atendidos integralmente devido à falta de estoque. Consulte o detalhamento completo em Meus Pedidos.",
        );
      }
      localStorage.removeItem(`@aqvex/products_${clientHash}`);
      localStorage.removeItem(`@aqvex/products_last_update_${clientHash}`);
      setCreatedOrderId(response.data.order.id);
      clearCart();
      setCurrentStep(2);
    } catch (error) {
      toast.error(error.response?.data?.error ?? "Erro ao realizar registro!");
    } finally {
      setLoading(false);
    }
  };

  const handleCpfCnpjChange = (value) => {
    setCpfCnpj(maskCpfCnpj(value));
  };

  // 🔹 Renderização principal
  return (
    <Flex
      width="100%"
      minH="100vh"
      paddingTop={isLargerThan768 ? "100px" : "120px"}
      justify="center"
    >
      <Flex
        direction="column"
        width="100%"
        maxW={isLargerThan768 ? "600px" : "90%"}
        align="center"
      >
        {/* Cabeçalho dos steps */}
        {!loadingOrder && (
          <HStack justify="center" spacing={8} mb={6}>
            {steps.map((label, index) => (
              <Flex key={index} direction="column" align="center">
                <Circle
                  size="35px"
                  bg={index <= currentStep ? PRIMARY_COLOR : "gray.300"}
                  color="white"
                >
                  {index + 1}
                </Circle>
                <Text
                  mt={2}
                  fontWeight={index === currentStep ? "bold" : "normal"}
                  color={index <= currentStep ? PRIMARY_COLOR : "gray.500"}
                >
                  {label}
                </Text>
              </Flex>
            ))}
          </HStack>
        )}

        {/* 🌀 Loading ao criar pedido automático */}
        {loadingOrder && (
          <Flex
            direction="column"
            align="center"
            justify="center"
            textAlign="center"
            gap={4}
            py={10}
          >
            <Spinner size="xl" color={PRIMARY_COLOR} />
            <Text fontSize="lg" mt={4}>
              Processando seu pedido...
            </Text>
          </Flex>
        )}

        {/* Erro ao criar pedido para usuário já logado */}
        {!loadingOrder && user?.info?.email && orderError && (
          <Flex direction="column" align="center" gap={4} mt={4} textAlign="center">
            <Text color="red.500" fontWeight="bold">
              Não foi possível concluir seu pedido
            </Text>
            <Text>{orderError}</Text>
            <Button
              backgroundColor={PRIMARY_COLOR}
              w="250px"
              onClick={createOrderForLoggedUser}
              isLoading={loadingOrder}
            >
              Tentar novamente
            </Button>
          </Flex>
        )}

        {/* Step 1 - Identificação */}
        {!loadingOrder &&
          currentStep === 0 &&
          !(user?.info?.email && orderError) && (
          <Flex direction="column" align="center" gap={4} mt={4}>
            <Text textAlign="center">
              Pra continuar com o pedido, digite o seu CPF/CNPJ
            </Text>
            <Input
              placeholder="CPF/CNPJ"
              value={cpfCnpj}
              onChange={(e) => handleCpfCnpjChange(e.target.value)}
              maxW="250px"
              padding="15px"
            />
            <Button
              backgroundColor={PRIMARY_COLOR}
              w="250px"
              isDisabled={!cpfCnpj}
              onClick={verifyCpfCnpj}
              isLoading={loading}
            >
              Continuar
            </Button>
          </Flex>
        )}

        {/* --- NOVO BLOCO: STEP INTERMEDIÁRIO DE LOGIN PARA CLIENTE EXISTENTE --- */}
        {!loadingOrder && currentStep === "login" && (
          <Flex direction="column" align="center" gap={4} mt={4}>
            <Text textAlign="center">
              Cliente encontrado! Digite sua senha para continuar o pedido.
            </Text>

            <Text fontSize="sm" color="gray.600">
              Entrando como: <b>{clientFoundData?.email}</b>
            </Text>

            <Input
              placeholder="Senha"
              type="password"
              value={passwordLogin}
              onChange={(e) => setPasswordLogin(e.target.value)}
              maxW="250px"
              padding="15px"
            />

            <Button
              backgroundColor={PRIMARY_COLOR}
              w="250px"
              isDisabled={!passwordLogin}
              onClick={loginAndCreateOrder}
              isLoading={loading}
            >
              Entrar e continuar
            </Button>
          </Flex>
        )}
        {/* --------------------------------------------------------------------- */}

        {/* Step 2 - Cadastro */}
        {!loadingOrder && currentStep === 1 && (
          <Flex
            direction="column"
            align="center"
            gap={4}
            mt={4}
            width="100%"
            as="form"
            onSubmit={registerClient}
          >
            {/* campos de cadastro iguais */}
            {[
              {
                label: "Nome completo",
                value: form.name,
                placeholder: "Digite seu nome",
                setter: (v) => setForm((prev) => ({ ...prev, name: v })),
                required: true,
              },
              {
                label: "E-mail de Acesso do Aplicativo",
                value: form.email,
                placeholder: "Digite seu e-mail",
                setter: (v) => setForm((prev) => ({ ...prev, email: v })),
                type: "email",
                required: true,
              },
              {
                label: "Telefone",
                value: form.phone,
                placeholder: "Digite seu telefone",
                setter: (v) =>
                  setForm((prev) => ({
                    ...prev,
                    phone: maskPhone(v),
                  })),
                required: true,
              },
              {
                label: "Instagram",
                value: form.instagram,
                placeholder: "@seuperfil",
                setter: (v) => setForm((prev) => ({ ...prev, instagram: v })),
                required: false,
              },
              {
                label: "CPF / CNPJ",
                value: cpfCnpj,
                placeholder: "Digite seu CPF ou CNPJ",
                setter: setCpfCnpj, // supondo que você tenha esse hook
                disabled: true,
              },
              {
                label: "Senha de Acesso do Aplicativo",
                value: form.password,
                placeholder: "Digite sua senha",
                setter: (v) => setForm((prev) => ({ ...prev, password: v })),
                type: "password",
                required: true,
              },
            ].map((field, idx) => (
              <Flex key={idx} direction="column" width="100%" align="center">
                <Text w="300px">{field.label}</Text>
                <Input
                  value={field.value}
                  isReadOnly={field.readOnly}
                  placeholder={field.placeholder}
                  onChange={
                    field.setter
                      ? (e) => field.setter(e.target.value)
                      : undefined
                  }
                  padding="15px"
                  maxW="300px"
                  type={field.type ?? "text"}
                  disabled={field.disabled}
                  required={field.required}
                />
              </Flex>
            ))}

            <Button
              backgroundColor={PRIMARY_COLOR}
              w="300px"
              isDisabled={
                !form.email ||
                !form.password ||
                !form.phone ||
                !form.name ||
                !cpfCnpj
              }
              type="submit"
              isLoading={loading}
            >
              Continuar
            </Button>
          </Flex>
        )}

        {/* Step 3 - Confirmação */}
        {!loadingOrder && currentStep === 2 && (
          <Flex
            direction="column"
            align="center"
            justify="center"
            textAlign="center"
            gap={4}
            py={10}
          >
            <FaCheckCircle size={60} color="green" />
            <Text fontSize="2xl" fontWeight="bold">
              Seu pedido foi enviado
            </Text>
            <Text>
              Seu pedido foi realizado com sucesso! Acompanhe seu pedido por
              e-mail.
            </Text>
            <Button
              backgroundColor={PRIMARY_COLOR}
              w="300px"
              onClick={() => navigate(`/pedido/${createdOrderId}`)}
            >
              Acompanhar pedido
            </Button>
            <Button
              w="300px"
              onClick={() => navigate(`/catalogo/${clientHash}/${sellerId}`)}
            >
              Voltar
            </Button>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
